import { memo, useMemo } from 'react'

import { FileItem } from '@inno-sdks/files'
import { ButtonColors, ButtonVariants } from '@inno-ui/button'
import { FileIconSizes } from '@inno-ui/file-icons'
import {
  ArrowDownRectangleLineIcon,
  ArrowUpRightIcon,
  InformationCircleLineIcon,
  PrinterLineIcon,
  TrashLineIcon
} from '@inno-ui/icons'
import { Modal, ModalProps, ModalVariants } from '@inno-ui/modal'
import { useGetFileItem } from 'modules/desktop/modules/files-integration/api'
import { useTranslation } from 'react-i18next'

import {
  emptyCharacter,
  maxItemNameLength,
  mimeTypes
} from '../../../constants'
import {
  useDownloadFile,
  useItemVisibility,
  useOpenItem,
  usePermissions,
  usePrintFile
} from '../../../hooks'
import { isClusterOfficeFile, sleep } from '../../../utils'
import { ItemIcon } from '../../GridViewItem/components/ItemIcon'
import { FilePreview } from '../Preview'
import { FileProperties } from '../Properties'
import { useItemModalOperations } from '../hooks'
import { useItemModal } from '../hooks/useItemModal'

export const FileModal = memo(() => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop'
  })

  const { itemId, close } = useItemModal()

  const { data: item, isLoading } = useGetFileItem<FileItem>(itemId)
  const downloadFile = useDownloadFile()
  const printFile = usePrintFile()
  const { shouldShowPrint } = useItemVisibility(item)
  const openItem = useOpenItem({ inNewTab: true })

  const { handleMoveToTrash, handleTitleChange, modalResetKey } =
    useItemModalOperations(item)
  const { canDownload, canWrite } = usePermissions(item?.role)

  const isPreviewAvailable = useMemo(
    () =>
      item &&
      item?.metaData.presignedUrl &&
      (isClusterOfficeFile(item) ||
        [
          ...Object.values(mimeTypes.image),
          ...Object.values(mimeTypes.video),
          ...Object.values(mimeTypes.audio),
          mimeTypes.plainDocument.vtt,
          mimeTypes.plainDocument.html
        ].includes(item.metaData.mimeType)),
    [item]
  )

  const modalParams = useMemo<ModalProps>(
    () => ({
      variant: ModalVariants.Full,
      zIndex: 9,
      maxWidth: 1400,
      maxHeight: 831,
      contentFullWidth: true,
      isOpen: !!itemId,
      closeOnClickOutside: false,
      onClose: close,
      rightSideBarWidth: 600,
      sidebarTabId: 1,
      isSidebarOpen: true,
      sidebarTabs: [
        {
          isLoading,
          value: 1,
          label: t('properties'),
          icon: InformationCircleLineIcon,
          body: <FileProperties />
        }
      ],
      layers: [
        {
          actionsFullWidth: false,
          bodyPaddings: false,
          header: {
            isLoading,
            button: {
              label: t('open-in-new-tab'),
              icon: ArrowUpRightIcon,
              bold: false,
              onClick: () => openItem(item!)
            }
          },
          title: item?.name.replaceAll(emptyCharacter, ''),
          onChangeTitle: !canWrite ? undefined : handleTitleChange,
          titleCharacterLimit: maxItemNameLength,
          isBodyLoading: isLoading,
          icon:
            item &&
            (() => <ItemIcon item={item} size={FileIconSizes.Size48} />),
          body: <FilePreview />,
          actionsLeft: [
            {
              label: t('move-to-trash'),
              icon: TrashLineIcon,
              color: ButtonColors.Red,
              variant: ButtonVariants.Ghost,
              onClick: handleMoveToTrash
            }
          ],
          actionsRight: isPreviewAvailable
            ? [
                ...(shouldShowPrint && canDownload
                  ? [
                      {
                        label: t('print'),
                        icon: PrinterLineIcon,
                        onClick: async () => {
                          printFile(item!.id)
                          await sleep(500)
                          close()
                        }
                      }
                    ]
                  : []),
                ...(canDownload
                  ? [
                      {
                        label: t('download'),
                        icon: ArrowDownRectangleLineIcon,
                        onClick: async () => {
                          downloadFile(item!.id)
                          await sleep(500)
                          close()
                        }
                      }
                    ]
                  : [])
              ]
            : undefined
        }
      ]
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemId, item?.name, isLoading, close]
  )

  return <Modal key={`${itemId}-${modalResetKey}`} {...modalParams} />
})
